import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, Input, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import {
  Autoformat,
  AutoLink,
  BlockQuote,
  Bold,
  ClassicEditor,
  EditorConfig,
  Essentials,
  Heading,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  Paragraph,
  PasteFromOffice,
  SelectAll,
  TextTransformation,
  Underline,
  Undo
} from 'ckeditor5';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { BaseServices } from '../../kuba.services';
import {
  AddOnboardingCommentRequest,
  FlexiOnboardingAggregate,
  FlexiOnboardingCommentType,
  FlexiOnboardingRemovedDefaultStep,
  FlexiOnboardingResponsibleParty,
  FlexiOnboardingStatus,
  FlexiOnboardingStep,
  FlexiOnboardingStepComment,
  NewFlexiOnboardingStepRequest
} from './flexi-onboarding.models';
import { FlexiOnboardingService } from './flexi-onboarding.service';

@Component({
  selector: 'flexi-onboarding-wizard',
  templateUrl: './onboarding-wizard.component.html',
  styleUrls: ['./flexi-onboarding.scss'],
  standalone: false
})
export class OnboardingWizardComponent implements OnInit {
  private readonly notifications = inject(NotificationService);

  @Input() aggregate!: FlexiOnboardingAggregate;

  Editor = ClassicEditor;
  editorConfig: EditorConfig = {
    language: 'en',
    toolbar: {
      items: [
        'undo', 'redo', '|', 'heading', '|',
        'bold', 'italic', 'underline', '|',
        'link', 'blockQuote', '|',
        'bulletedList', 'numberedList', 'outdent', 'indent'
      ],
      shouldNotGroupWhenFull: true
    },
    plugins: [
      Autoformat, AutoLink, BlockQuote, Bold, Essentials, Heading,
      Indent, IndentBlock, Italic, Link, List, ListProperties,
      Paragraph, PasteFromOffice, SelectAll, TextTransformation, Underline, Undo
    ],
    link: { addTargetToExternalLinks: true, defaultProtocol: 'https://' }
  };

  activeIndex = 0;
  editableModel = '';
  saving = false;
  errorMessage: string | null = null;

  comments: FlexiOnboardingStepComment[] = [];
  loadingComments = false;
  newComment = '';
  newCommentIsPublic = false;
  /** Comment classification — drives the conditional Responsible / Deadline inputs. */
  newCommentType: FlexiOnboardingCommentType = FlexiOnboardingCommentType.Note;
  /** Who owns the agreed action. Customer is the default per spec. */
  newCommentResponsible: FlexiOnboardingResponsibleParty = FlexiOnboardingResponsibleParty.Customer;
  /** Optional ISO date yyyy-MM-dd for an Action comment. Bound to <input type="date">. */
  newCommentDeadline = '';

  /** Convenience enum bindings for the template (Angular can't access TS enums directly). */
  readonly CommentType = FlexiOnboardingCommentType;
  readonly Responsible = FlexiOnboardingResponsibleParty;

  /** Latch toggled while a comment POST is in flight to block double-submits. */
  postingComment = false;
  /** Per-comment deleting flag so the trash button can show a spinner / stay disabled. */
  deletingCommentIds = new Set<number>();

  addStepOpen = false;
  addStepInsertAt = 0;
  addStepInput: NewFlexiOnboardingStepRequest = {
    insertAtPosition: 0, title: '', shortLabel: '', subtitle: null, templateText: null
  };

  /** Default catalog steps the CSM removed and can restore. Refreshed after each mutation. */
  removedDefaults: FlexiOnboardingRemovedDefaultStep[] = [];
  /** ID of the comment author (we get the row's CreatedBy to gate the trash icon). */
  readonly currentUserId = Number(BaseServices.UserId);
  /**
   * Admins can delete any comment. AbsoluteroleId stays '1' in R-mode, so
   * this covers admin-direct + admin-in-R-mode. Portal is editor-equivalent
   * in Flexi and is excluded.
   */
  readonly isPrivilegedUser: boolean =
    String(BaseServices.AbsoluteroleId) === '1';

  readonly Status = FlexiOnboardingStatus;

  constructor(
    private service: FlexiOnboardingService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location
  ) {}

  /**
   * Back to the history grid. The wizard sits one segment deeper than the
   * grid, so we navigate to the parent rather than pushing a new route.
   */
  backToList(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  ngOnInit(): void {
    this.activeIndex = Math.min(this.aggregate.currentStep, this.aggregate.steps.length - 1);
    this.applyActiveStep();
    this.refreshRemovedDefaults();
  }

  /** Fetches the list of removed catalog defaults so the CSM can restore one. */
  private refreshRemovedDefaults(): void {
    // Nothing to compare against until the record exists.
    if (this.isDraft) { this.removedDefaults = []; return; }

    this.service.listRemovedDefaultSteps(this.businessId, this.formId).subscribe({
      next: list => { this.removedDefaults = list ?? []; },
      error: () => { /* non-blocking — keep the existing list */ }
    });
  }

  canDeleteComment(c: FlexiOnboardingStepComment): boolean {
    if (this.isPrivilegedUser) return true;
    return c.createdBy != null && c.createdBy === this.currentUserId;
  }

  // -------- derived state --------

  get businessId(): number { return this.aggregate.businessId; }

  /**
   * Completion wording depends on which form this is. The business's first form
   * IS the onboarding, so finishing it reads as "Onboarding completed". Every
   * later form — a new one built from a previous version, or a re-completion of
   * an existing one — is a periodic re-assessment and is worded that way.
   */
  get completedBannerTitleKey(): string {
    return this.aggregate.isFirstForm
      ? 'FLEXI_ONBOARDING.COMPLETED_BANNER_TITLE'
      : 'FLEXI_ONBOARDING.ASSESSMENT_COMPLETED_BANNER_TITLE';
  }

  get completeActionKey(): string {
    return this.aggregate.isFirstForm
      ? 'FLEXI_ONBOARDING.ACTIONS.COMPLETE'
      : 'FLEXI_ONBOARDING.ACTIONS.COMPLETE_ASSESSMENT';
  }

  private get completeConfirmKey(): string {
    return this.aggregate.isFirstForm
      ? 'FLEXI_ONBOARDING.CONFIRM.COMPLETE'
      : 'FLEXI_ONBOARDING.CONFIRM.COMPLETE_ASSESSMENT';
  }
  /** The form record being authored — every write targets this record only. */
  get formId(): number { return this.aggregate.id; }

  /**
   * True while the CSM is on a form that has not been created yet (id 0).
   *
   * "New" deliberately writes nothing: the record is created by the first save,
   * so opening New and navigating away leaves no row behind in the history grid.
   */
  get isDraft(): boolean { return !this.aggregate.id; }

  /**
   * Creates the record if this is still a draft, then completes. Every write
   * path funnels through here so the caller can stay unaware of draft state.
   *
   * The server rebuilds the new record from the same template the draft was
   * generated from, so the content the CSM has been looking at is what gets
   * written; we then graft the assigned ids onto the local aggregate rather
   * than replacing it, which would discard edits typed but not yet saved.
   */
  private ensurePersisted(): Observable<void> {
    if (!this.isDraft) return of(undefined);

    return this.service.createForm(this.businessId).pipe(
      tap(created => this.adoptCreatedRecord(created)),
      switchMap(() => of(undefined))
    );
  }

  /**
   * Adopts the ids of a just-created record onto the in-memory draft and swaps
   * the URL from /new to /{id}.
   *
   * `Location.replaceState` rather than `Router.navigate`: navigating would
   * re-run the resolver and rebuild this component, throwing away the step the
   * CSM is standing on and anything they have typed. Steps are matched by
   * stepKey, which is stable between the draft and the record built from the
   * same template.
   */
  private adoptCreatedRecord(created: FlexiOnboardingAggregate): void {
    this.aggregate.id = created.id;
    this.aggregate.isFirstForm = created.isFirstForm;

    const idsByStepKey = new Map(created.steps.map(s => [s.stepKey, s.id]));
    for (const step of this.aggregate.steps) {
      const id = idsByStepKey.get(step.stepKey);
      if (id != null) step.id = id;
    }

    const url = this.router.createUrlTree(['../', created.id], { relativeTo: this.route }).toString();
    this.location.replaceState(url);

    // Only meaningful once the record exists — a draft has no server state to
    // compare the catalog against.
    this.refreshRemovedDefaults();
  }
  get currentStep(): FlexiOnboardingStep | null { return this.aggregate.steps[this.activeIndex] ?? null; }
  get isCompleted(): boolean { return this.aggregate.status === FlexiOnboardingStatus.Completed; }
  get isLastStep(): boolean { return this.activeIndex === this.aggregate.steps.length - 1; }

  // -------- rendering helpers --------

  interpolated(text: string | null): string {
    if (!text) return '';
    return text
      .split('{BusinessName}').join(BaseServices.BusinessName || '')
      .split('{OrganisationNumber}').join('')
      .split('{ValidUntil}').join(this.aggregate.validUntil || '');
  }

  renderTemplate(text: string | null): string {
    if (!text) return '';
    return this.interpolated(text)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/(?:<li>.*<\/li>\n?)+/g, m => `<ul>${m}</ul>`)
      .replace(/\n\n+/g, '</p><p>')
      .replace(/^(.+)$/m, '<p>$1</p>');
  }

  hasAuthoredContent(): boolean {
    return this.stripHtml(this.editableModel).trim().length > 0;
  }

  private stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ');
  }

  // -------- navigation --------

  jumpTo(index: number): void {
    if (index < 0 || index >= this.aggregate.steps.length) return;
    this.persistDraftLocally();
    this.activeIndex = index;
    this.applyActiveStep();
  }

  prev(): void { this.jumpTo(this.activeIndex - 1); }

  private applyActiveStep(): void {
    const step = this.currentStep;
    if (!step) return;
    this.editableModel = step.editableText ?? '';
    this.loadComments(step.stepKey);
  }

  private persistDraftLocally(): void {
    const step = this.currentStep;
    if (step) step.editableText = this.editableModel || null;
  }

  // -------- save flow --------

  save(): void {
    const step = this.currentStep;
    if (!step) return;
    this.saving = true;
    this.errorMessage = null;
    const text = this.hasAuthoredContent() ? this.editableModel : null;
    this.ensurePersisted().pipe(
      switchMap(() => this.service.updateStep(this.businessId, this.formId, step.stepKey, text))
    ).subscribe({
      next: () => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        step.editableText = text;
        this.saving = false;
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_ONBOARDING.ERRORS.SAVE_FAILED');
      }
    });
  }

  saveAndNext(): void {
    const step = this.currentStep;
    if (!step) return;
    // Remember where we are right now — relying on `agg.currentStep` for the
    // post-save navigation is fragile when the CSM has previously visited a
    // later step (BE stores max-visited progress in CurrentStep, which can
    // outlive a backward jump in the stepper). Use the active index instead.
    const fromIndex = this.activeIndex;
    this.saving = true;
    this.errorMessage = null;
    const text = this.hasAuthoredContent() ? this.editableModel : null;
    this.ensurePersisted().pipe(
      switchMap(() => this.service.saveAndNext(this.businessId, this.formId, step.stepKey, text))
    ).subscribe({
      next: agg => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.aggregate = agg;
        // Always advance by one from the saved step, clamped to the last
        // index — so "Save and next" on step 3 lands on step 4 even if BE
        // is on an older binary that doesn't update CurrentStep correctly.
        this.activeIndex = Math.min(fromIndex + 1, agg.steps.length - 1);
        this.applyActiveStep();
        this.saving = false;
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_ONBOARDING.ERRORS.SAVE_AND_NEXT_FAILED');
      }
    });
  }

  complete(): void {
    const step = this.currentStep;
    if (!step) return;
    this.persistDraftLocally();
    const text = this.hasAuthoredContent() ? this.editableModel : null;
    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant(this.completeConfirmKey),
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.saving = true;
        this.errorMessage = null;
        // Persist the step the CSM is standing on before publishing.
        // persistDraftLocally() only updates the in-memory aggregate, while the
        // server merges FinalText from its own EditableText — so without this
        // save, text typed and not saved was silently missing from the
        // published document and the archived PDF.
        this.ensurePersisted().pipe(
          switchMap(() => this.service.updateStep(this.businessId, this.formId, step.stepKey, text)),
          switchMap(() => this.service.complete(this.businessId, this.formId))
        ).subscribe({
          next: agg => {
                         this.notifications.success('UI_NOTIFICATIONS.SAVED'); this.applyAggregate(agg); this.saving = false; },
          error: (err: HttpErrorResponse) => {
            this.saving = false;
            this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_ONBOARDING.ERRORS.COMPLETE_FAILED');
          }
        });
      }
    });
  }

  private applyAggregate(agg: FlexiOnboardingAggregate): void {
    this.aggregate = agg;
    if (this.activeIndex >= agg.steps.length) this.activeIndex = Math.max(0, agg.steps.length - 1);
    this.applyActiveStep();
  }

  // -------- comments --------

  private loadComments(stepKey: string): void {
    this.comments = [];

    // A draft has no server-side step to hang comments off yet.
    if (this.isDraft) {
      this.loadingComments = false;
      return;
    }

    this.loadingComments = true;
    this.service.listComments(this.businessId, this.formId, stepKey).subscribe({
      next: list => { this.comments = list; this.loadingComments = false; },
      error: () => { this.loadingComments = false; }
    });
  }

  addComment(): void {
    const step = this.currentStep;
    const text = (this.newComment || '').trim();
    // Guard against double-clicks: the latch is the source of truth, the [disabled]
    // binding mirrors it on the template side.
    if (!step || !text || this.postingComment) return;

    // Action/Decision comments are always shown to the customer — force IsPublic
    // here to match the BE invariant. The checkbox is also disabled in the UI.
    const isAction = this.newCommentType === FlexiOnboardingCommentType.Action;
    const isDecision = this.newCommentType === FlexiOnboardingCommentType.Decision;
    const isPublic = this.newCommentIsPublic || isAction || isDecision;

    const body: AddOnboardingCommentRequest = {
      commentText: text,
      isPublic,
      commentType: this.newCommentType,
      // Only send Responsible + Deadline for Actions; BE drops them otherwise
      // but keeping the payload tight makes the network log easier to read.
      responsibleParty: isAction ? this.newCommentResponsible : null,
      deadline: isAction && this.newCommentDeadline ? this.newCommentDeadline : null
    };

    this.postingComment = true;
    this.persistDraftLocally();
    this.ensurePersisted().pipe(
      switchMap(() => this.service.addComment(this.businessId, this.formId, step.stepKey, body))
    ).subscribe({
      next: created => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.comments = [created, ...this.comments];
        this.newComment = '';
        this.newCommentIsPublic = false;
        this.newCommentType = FlexiOnboardingCommentType.Note;
        this.newCommentResponsible = FlexiOnboardingResponsibleParty.Customer;
        this.newCommentDeadline = '';
        this.postingComment = false;
      },
      error: (err: HttpErrorResponse) => {
        this.postingComment = false;
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_ONBOARDING.ERRORS.ADD_COMMENT_FAILED');
      }
    });
  }

  /**
   * Whether the Type selector resolves to an Action (used by the template to
   * conditionally show the Responsible + Deadline fields and force IsPublic).
   */
  get isNewCommentAction(): boolean {
    return this.newCommentType === FlexiOnboardingCommentType.Action;
  }

  /** Force IsPublic on for non-Note comment types so the checkbox can't lie. */
  onCommentTypeChange(): void {
    if (this.newCommentType !== FlexiOnboardingCommentType.Note) {
      this.newCommentIsPublic = true;
    }
  }

  deleteComment(comment: FlexiOnboardingStepComment): void {
    const step = this.currentStep;
    if (!step || this.deletingCommentIds.has(comment.id)) return;
    if (!this.canDeleteComment(comment)) return;

    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_ONBOARDING.CONFIRM.DELETE_COMMENT'),
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.deletingCommentIds.add(comment.id);
        this.service.deleteComment(this.businessId, this.formId, step.stepKey, comment.id).subscribe({
          next: () => {
            this.notifications.success('UI_NOTIFICATIONS.DELETED');
            this.comments = this.comments.filter(c => c.id !== comment.id);
            this.deletingCommentIds.delete(comment.id);
          },
          error: (err: HttpErrorResponse) => {
            this.deletingCommentIds.delete(comment.id);
            this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_ONBOARDING.ERRORS.DELETE_COMMENT_FAILED');
          }
        });
      }
    });
  }

  /** Privileged-user action — re-open a completed onboarding for further edits. */
  reopen(): void {
    if (!this.isCompleted || this.saving) return;
    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_ONBOARDING.CONFIRM.REOPEN'),
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.saving = true;
        this.errorMessage = null;
        this.service.reopen(this.businessId, this.formId).subscribe({
          next: agg => { this.applyAggregate(agg); this.saving = false; },
          error: (err: HttpErrorResponse) => {
            this.saving = false;
            this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_ONBOARDING.ERRORS.REOPEN_FAILED');
          }
        });
      }
    });
  }

  /** Restore a default catalog step the CSM removed earlier. */
  restoreDefaultStep(s: FlexiOnboardingRemovedDefaultStep): void {
    if (this.saving) return;
    this.saving = true;
    this.errorMessage = null;
    this.persistDraftLocally();
    this.ensurePersisted().pipe(
      switchMap(() => this.service.restoreDefaultStep(this.businessId, this.formId, s.stepKey))
    ).subscribe({
      next: agg => {
        this.aggregate = agg;
        // The restored step is appended at the end — jump to it so the CSM
        // can immediately review or edit it.
        this.activeIndex = agg.steps.length - 1;
        this.applyActiveStep();
        this.refreshRemovedDefaults();
        this.saving = false;
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_ONBOARDING.ERRORS.RESTORE_STEP_FAILED');
      }
    });
  }

  // -------- template helpers --------

  insertTemplate(): void {
    if (!this.currentStep || !this.currentStep.templateText) return;
    const apply = () => {
      this.editableModel = this.renderTemplate(this.currentStep!.templateText);
    };
    if (!this.hasAuthoredContent()) { apply(); return; }
    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_ONBOARDING.CONFIRM.REPLACE_TEMPLATE'),
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: apply
    });
  }

  clearOverride(): void {
    if (!this.hasAuthoredContent()) { this.editableModel = ''; return; }
    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_ONBOARDING.CONFIRM.CLEAR_OVERRIDE'),
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => { this.editableModel = ''; }
    });
  }

  // -------- step management --------

  openAddStep(insertAt: number): void {
    this.addStepInsertAt = insertAt;
    this.addStepInput = { insertAtPosition: insertAt, title: '', shortLabel: '', subtitle: null, templateText: null };
    this.addStepOpen = true;
  }

  cancelAddStep(): void { this.addStepOpen = false; }

  get addStepLabel(): string {
    const total = this.aggregate.steps.length;
    if (this.addStepInsertAt >= total) return this.translate.instant('FLEXI_ONBOARDING.ADD_PANEL.INSERTING_AT_END');
    if (this.addStepInsertAt <= 0) return this.translate.instant('FLEXI_ONBOARDING.ADD_PANEL.INSERTING_AT_START');
    const before = this.aggregate.steps[this.addStepInsertAt - 1];
    const after = this.aggregate.steps[this.addStepInsertAt];
    return this.translate.instant('FLEXI_ONBOARDING.ADD_PANEL.INSERTING_BETWEEN', { before: before.title, after: after.title });
  }

  submitAddStep(): void {
    const title = (this.addStepInput.title || '').trim();
    if (!title) return;
    const insertAt = this.addStepInsertAt;
    const body: NewFlexiOnboardingStepRequest = {
      insertAtPosition: insertAt,
      title,
      shortLabel: (this.addStepInput.shortLabel || title).trim().slice(0, 32),
      subtitle: (this.addStepInput.subtitle || '').trim() || null,
      templateText: (this.addStepInput.templateText || '').trim() || null
    };
    this.saving = true;
    this.errorMessage = null;
    this.persistDraftLocally();
    this.ensurePersisted().pipe(
      switchMap(() => this.service.addStep(this.businessId, this.formId, body))
    ).subscribe({
      next: agg => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.aggregate = agg;
        this.activeIndex = Math.min(insertAt, agg.steps.length - 1);
        this.applyActiveStep();
        this.addStepOpen = false;
        this.refreshRemovedDefaults();
        this.saving = false;
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_ONBOARDING.ERRORS.ADD_STEP_FAILED');
      }
    });
  }

  removeStepAt(event: Event, index: number): void {
    event.stopPropagation();
    const step = this.aggregate.steps[index];
    if (!step || this.aggregate.steps.length <= 1) return;
    const warningParts: string[] = [];
    if (step.isDefault) warningParts.push(this.translate.instant('FLEXI_ONBOARDING.CONFIRM.REMOVE_DEFAULT_WARNING'));
    const message = this.translate.instant('FLEXI_ONBOARDING.CONFIRM.REMOVE_STEP', { title: step.title })
      + (warningParts.length ? '<br><br>' + warningParts.join('<br>') : '');

    // Snapshot the position pointers BEFORE the request fires so the
    // post-response navigation math survives an out-of-order response.
    const removedIndex = index;
    const previousActive = this.activeIndex;

    this.confirmationService.confirm({
      header: this.translate.instant('CONFIRMATION'),
      message,
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.saving = true;
        this.errorMessage = null;
        this.persistDraftLocally();
        this.ensurePersisted().pipe(
          switchMap(() => this.service.removeStep(this.businessId, this.formId, step.stepKey))
        ).subscribe({
          next: agg => {
            this.aggregate = agg;
            // Keep the wizard on the same logical step the CSM was editing:
            //   - removed step is BEFORE the active one → shift activeIndex down by 1
            //   - removed step IS the active one → stay at the same index
            //     (which is now whatever step shifted up to take this slot),
            //     clamped to the new last index in case we deleted the tail
            //   - removed step is AFTER the active one → no shift
            let newActive = previousActive;
            if (removedIndex < previousActive) newActive -= 1;
            this.activeIndex = Math.max(0, Math.min(newActive, agg.steps.length - 1));
            this.applyActiveStep();
            this.refreshRemovedDefaults();
            this.saving = false;
          },
          error: (err: HttpErrorResponse) => {
            this.saving = false;
            this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_ONBOARDING.ERRORS.REMOVE_STEP_FAILED');
          }
        });
      }
    });
  }

  // -------- PDF --------

  downloadOhsAgreementPdf(): void {
    this.service.downloadOhsAgreementPdf(this.businessId, this.formId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ohs-agreement.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      },
      error: (err: HttpErrorResponse) => {
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_ONBOARDING.ERRORS.PDF_FAILED');
      }
    });
  }
}
